// PerEmployeeMetricScoreTable
// ----------------------------
// The scrollable "Per-employee scores" table shared by:
//   • SubMetricsManagePanel → PerEmployeeScoresSection (custom-metric Scores tab)
//   • EmployeeScorePage      → the inline per-metric scores block (system /
//                              task_question metric sub-tabs)
//
// Both call sites pre-compute the sorted + searched row list themselves and
// hand it in here already projected to a single metric. This component owns
// only the presentation: the sticky-header table with Employee / Store
// columns followed by the three numeric columns (Raw value, Points, Weighted).
//
// Every header is click-sortable via the NumSortTh idiom both sites used
// inline; the sort key/dir/handler are passed through verbatim so each site
// keeps its own sort state. Both sites order rows with compareMetricScoreRows
// below, so the two tables sort identically. `searchTerm` is only used to fill
// in the "No employees match …" empty-row message exactly as before.

import { NumSortTh } from '../utils/sortableHeader';
import type { TableSheet } from '../utils/tableXlsx';
import { compareSortText } from './dashboard/useTableSort';

// Which column the table is sorted by — any of its eight columns.
export type MetricScoreSortKey =
  | 'employee_name' | 'employee_code' | 'store_name' | 'store_code' | 'store_address'
  | 'value' | 'points' | 'contribution';

const TEXT_SORT_KEYS = new Set<MetricScoreSortKey>([
  'employee_name', 'employee_code', 'store_name', 'store_code', 'store_address',
]);

// One already-projected row. Both call sites flatten their own row shape into
// this — value/points/contribution come straight off the metric the row was
// projected to.
export interface PerEmployeeMetricScoreRow {
  employee_code: string;
  employee_name: string;
  store_name: string;
  store_code: string;
  store_address: string;
  value: number | null;
  points: number;
  contribution: number;
}

/** Direction a column starts in on its first click: text A→Z, numbers high → low. */
export const initialMetricScoreSortDir = (key: MetricScoreSortKey): 'asc' | 'desc' =>
  TEXT_SORT_KEYS.has(key) ? 'asc' : 'desc';

/**
 * The comparator both call sites sort with. Blanks (no raw value, no store)
 * sink to the bottom in either direction; numbers compare numerically; text
 * compares locale- and number-aware ("S2" before "S10").
 */
export const compareMetricScoreRows = (
  a: PerEmployeeMetricScoreRow,
  b: PerEmployeeMetricScoreRow,
  key: MetricScoreSortKey,
  dir: 'asc' | 'desc',
): number => {
  const av = a[key];
  const bv = b[key];
  const aMissing = av === null || av === undefined || av === '';
  const bMissing = bv === null || bv === undefined || bv === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  const sign = dir === 'asc' ? 1 : -1;
  if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign;
  return compareSortText(String(av), String(bv)) * sign;
};

// Header text per column — read by the table header below and by the Excel
// export (metricScoreSheet), so the file's headers cannot drift from the screen's.
const COLUMN_LABEL = {
  employee_name: 'Employee Name',
  employee_code: 'Employee Code',
  store_name: 'Store Name',
  store_code: 'Store Code',
  store_address: 'Store Address',
  value: 'Raw value',
  points: 'Points',
} as const;
const weightedLabel = (weightPct: number): string => `Weighted (× ${weightPct}%)`;

/**
 * The table as an Excel sheet: the same eight columns in the same order, and
 * the rows exactly as handed in (the call site has already sorted and searched
 * them). A '—' on screen is an empty cell; Points and Weighted keep the two
 * decimals the table shows.
 */
export const metricScoreSheet = (
  name: string,
  rows: PerEmployeeMetricScoreRow[],
  weightPct: number,
): TableSheet => ({
  name,
  headers: [
    COLUMN_LABEL.employee_name, COLUMN_LABEL.employee_code, COLUMN_LABEL.store_name,
    COLUMN_LABEL.store_code, COLUMN_LABEL.store_address,
    COLUMN_LABEL.value, COLUMN_LABEL.points, weightedLabel(weightPct),
  ],
  rows: rows.map((r) => [
    r.employee_name, r.employee_code, r.store_name, r.store_code, r.store_address,
    r.value, r.points, r.contribution,
  ]),
  numberFormats: { 6: '0.00', 7: '0.00' },
});

interface Props {
  /** Already sorted + search-filtered rows. Empty array renders the
   *  "No employees match …" message row. */
  rows: PerEmployeeMetricScoreRow[];
  /** Percentage shown in the "Weighted (× N%)" header. */
  weightPct: number;
  sortKey: MetricScoreSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (col: MetricScoreSortKey) => void;
  /** Current search query — only used to echo it back in the empty-row
   *  message ("No employees match “{searchTerm}”."). */
  searchTerm: string;
}

export default function PerEmployeeMetricScoreTable({
  rows, weightPct, sortKey, sortDir, onSort, searchTerm,
}: Props) {
  return (
    <div className="overflow-auto max-h-[70vh]">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <NumSortTh label={COLUMN_LABEL.employee_name} col="employee_name" align="left" sortKey={sortKey} sortDir={sortDir} onSort={onSort} sticky />
            <NumSortTh label={COLUMN_LABEL.employee_code} col="employee_code" align="left" sortKey={sortKey} sortDir={sortDir} onSort={onSort} sticky />
            <NumSortTh label={COLUMN_LABEL.store_name} col="store_name" align="left" sortKey={sortKey} sortDir={sortDir} onSort={onSort} sticky />
            <NumSortTh label={COLUMN_LABEL.store_code} col="store_code" align="left" sortKey={sortKey} sortDir={sortDir} onSort={onSort} sticky />
            <NumSortTh label={COLUMN_LABEL.store_address} col="store_address" align="left" sortKey={sortKey} sortDir={sortDir} onSort={onSort} sticky />
            <NumSortTh label={COLUMN_LABEL.value} col="value" sortKey={sortKey} sortDir={sortDir} onSort={onSort} sticky />
            <NumSortTh label={COLUMN_LABEL.points} col="points" sortKey={sortKey} sortDir={sortDir} onSort={onSort} sticky />
            <NumSortTh label={weightedLabel(weightPct)} col="contribution" sortKey={sortKey} sortDir={sortDir} onSort={onSort} sticky />
            {/* Quadrant column removed — quadrants are computed over every
                metric combined, so showing the same value against every
                per-metric row was misleading. The overall Score tab still
                surfaces it. */}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-3 py-6 text-center text-sm text-gray-500">
                No employees match “{searchTerm}”.
              </td>
            </tr>
          ) : rows.map((r) => (
            <tr key={r.employee_code}>
              <td className="px-3 py-2 font-medium text-gray-900">{r.employee_name}</td>
              <td className="px-3 py-2 text-gray-700">{r.employee_code}</td>
              <td className="px-3 py-2 text-gray-700">{r.store_name || '—'}</td>
              <td className="px-3 py-2 text-gray-700">{r.store_code || '—'}</td>
              <td className="px-3 py-2 text-gray-600">{r.store_address || '—'}</td>
              <td className="px-3 py-2 text-right text-gray-900">
                {r.value !== null ? r.value : '—'}
              </td>
              <td className="px-3 py-2 text-right">
                <span className={r.points > 0 ? 'font-medium text-gray-900' : 'text-gray-400'}>
                  {r.points.toFixed(2)}
                </span>
              </td>
              <td className="px-3 py-2 text-right font-semibold text-primary-700">
                {r.contribution.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
